import React, { useMemo, useRef } from "react"
import { FieldValues, RegisterOptions, useFormContext } from "react-hook-form"
import { Input } from "@mantine/core"

import { ErrorMessages, Label } from "@/components"
import classes from "@/components/TextWithPlaceholders/TextWithPlaceholders.module.css"
import { Placeholder } from "@/pages/texting/types"
import { CustomField, Placeholder as DjangoPlaceholder } from "@/types/djangio"
import { PERSONALIZED_PAGES_KEY } from "@/pages/campaign-form/components/campaigns/personalizedMessages/Messaging"

interface Props {
    name: string
    label: string
    rules: Omit<RegisterOptions<FieldValues, string>, "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled">
    withAsterisk?: boolean
    children: React.ReactNode
}

export const iconList = {
    "Contact Placeholders": {
        name: "user-circle",
        family: "far",
    },
    "Target Placeholders": {
        name: "link",
        family: "far",
    },
    "Conditional Placeholder": {
        name: "link",
        family: "far",
    },
    "Custom Questions": {
        name: "tasks",
        family: "far",
    },
    "Contacts Custom Fields": {
        name: "user-edit",
        family: "far",
    },
    "Custom Fields": {
        name: "building",
        family: "far",
    },
    "Footprint Data": {
        name: "link",
        family: "far",
    },
}

const { grassroots_contact: grassrootsContactType } = DjangIO.app.userdata.outbox.types.PlaceholderType

const formatPlaceholders = (
    personalizedPages: Record<string, CustomField[]>,
    placeholders: DjangoPlaceholder[],
    removeTargetPlaceholders: boolean,
) => {
    const validPlaceholders = placeholders?.filter((placeholder) => {
        const isContactPlaceholder =
            typeof placeholder.valid_for === "number"
                ? placeholder.valid_for === grassrootsContactType.value
                : placeholder.valid_for.includes(grassrootsContactType.value)

        if (!isContactPlaceholder && removeTargetPlaceholders) return false

        return true
    })

    const basePlaceholders =
        validPlaceholders?.map((placeholder) => ({
            group: placeholder.name,
            items: placeholder.items.map((item) => ({
                label: item.name,
                value: item.value,
            })),
        })) ?? []

    const customQuestions = [...(Object.values(personalizedPages)[0] ?? [])]
    if (customQuestions.length === 0) return basePlaceholders

    const customQuestionsPlaceholder = {
        group: "Custom Questions",
        items: customQuestions.map((question) => ({
            label: question.name,
            value: `{{${question.slug}}}`,
        })),
    }

    return [...basePlaceholders, customQuestionsPlaceholder]
}

export const useTextWithPlaceholders = (placeholders: DjangoPlaceholder[], removeTargetPlaceholders: boolean) => {
    const cursorTextRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 })
    const { getValues } = useFormContext()
    const personalizedPages = getValues(PERSONALIZED_PAGES_KEY) as Record<string, CustomField[]>
    const formattedPlaceholders = useMemo(
        () => formatPlaceholders(personalizedPages, placeholders, removeTargetPlaceholders),
        [personalizedPages, placeholders, removeTargetPlaceholders],
    )

    const handleTextSelection = (event: React.SyntheticEvent) => {
        const { selectionStart, selectionEnd } = event.target as HTMLTextAreaElement
        cursorTextRef.current = { start: selectionStart, end: selectionEnd }
    }

    return { formattedPlaceholders, handleTextSelection, cursorTextRef }
}

export const TextWithPlaceholders = ({ name, label, children, rules, withAsterisk = false }: Props) => {
    const { getFieldState } = useFormContext()
    const inputField = getFieldState(name)

    return (
        <div className={classes.messageContainer}>
            <Input.Label htmlFor={name} className={classes.label}>
                <Label label={label} withAsterisk={withAsterisk && !!rules?.required} />
            </Input.Label>
            {children}
            {Boolean(inputField.error) && <ErrorMessages fieldError={inputField.error} />}
        </div>
    )
}
